import React, { useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { exportAPI } from '../services/api';

// ── Colour palette ──────────────────────────────────────────────────────────
const PIE_COLORS = [
  '#1B5E3B','#2E7D52','#3b82f6','#f97316','#22c55e',
  '#eab308','#ec4899','#14b8a6','#f43f5e','#06b6d4','#94a3b8'
];

// ── Formatters ───────────────────────────────────────────────────────────────
function fmtINR(v) {
  const n = parseFloat(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}
function fmtY(v) {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000)   return `${(v / 100000).toFixed(0)}L`;
  return v.toLocaleString('en-IN');
}
function fmtPct(v) {
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${v}%`;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function KPICard({ label, value, sub, accent }) {
  return (
    <div style={S.kpiCard}>
      <div style={{ fontSize: 11, color: '#9b9b9b', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: accent || '#1a1a1a', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9b9b9b', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function TierBadge({ tier }) {
  const colors = { A: ['#ecfdf5','#059669'], B: ['#fffbeb','#d97706'], C: ['#fef2f2','#dc2626'] };
  const [bg, fg] = colors[tier] || ['#E8F6EE','#1B5E3B'];
  return (
    <span style={{ background: bg, color: fg, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
      {tier}
    </span>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e6e0', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12 }}>
      <div style={{ fontWeight: 600, color: '#1a1a1a', marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color, display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          <span>{p.name}:</span>
          <span style={{ fontWeight: 600 }}>₹{(parseFloat(p.value) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
        </div>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e6e0', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12, maxWidth: 200 }}>
      <div style={{ fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>{d.name}</div>
      <div style={{ color: '#1B5E3B' }}>{fmtINR(d.value)}</div>
      <div style={{ color: '#9b9b9b' }}>{d.payload.pct}% share</div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

export default function AnalyticsDashboard({ data, onExportExcel }) {
  const { kpis, trend, rankings, distribution, statusBreakdown, generatedAt, detailRows, sqlQueries } = data;
  const [exportingHtml,  setExportingHtml]  = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [rankPage, setRankPage]            = useState(0);
  const [sqlOpen,  setSqlOpen]             = useState(false);
  const [copied,   setCopied]              = useState(null);
  const PAGE = 10;

  const copySQL = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  const payload = { kpis, trend, rankings, distribution, detailRows, statusBreakdown, title: 'Sales Analysis', generatedAt };

  const handleHTML = async () => {
    setExportingHtml(true);
    try {
      const res = await exportAPI.analyticsHTML(payload);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/html' }));
      const a = document.createElement('a');
      a.href = url; a.download = `sales-analysis-${Date.now()}.html`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('HTML report failed.'); }
    finally { setExportingHtml(false); }
  };

  const handleExcel = async () => {
    setExportingXlsx(true);
    try {
      const res = await exportAPI.analyticsExcel(payload);
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url; a.download = `sales-analysis-${Date.now()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Excel export failed.'); }
    finally { setExportingXlsx(false); }
  };

  const periodStr = (() => {
    if (!kpis.periodStart && !kpis.periodEnd) return '';
    const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '';
    return `${fmt(kpis.periodStart)} – ${fmt(kpis.periodEnd)}`;
  })();

  const visibleRankings = rankings.slice(rankPage * PAGE, (rankPage + 1) * PAGE);

  return (
    <div style={S.wrap}>
      {/* ── Header ── */}
      <div style={{ ...S.headerCard, animation: 'sectionReveal 0.4s ease 0s both' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={S.analyticsIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
              </div>
              <h2 style={S.headerTitle}>Sales Analysis</h2>
            </div>
            <div style={{ fontSize: 13, color: '#6b6b6b' }}>
              {kpis.totalQuotations} quotation records
              {periodStr && <> &nbsp;·&nbsp; {periodStr}</>}
              &nbsp;·&nbsp; Generated {new Date(generatedAt).toLocaleString()}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {/* Interactive HTML report */}
            <button onClick={handleHTML} disabled={exportingHtml} className="btn-lift" style={S.htmlBtn}>
              {exportingHtml ? <span style={S.spinner} /> : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                </svg>
              )}
              {exportingHtml ? 'Generating…' : 'Interactive Report'}
            </button>
            {/* Raw data Excel */}
            <button onClick={handleExcel} disabled={exportingXlsx} className="btn-lift" style={S.excelBtn}>
              {exportingXlsx ? <span style={S.spinner} /> : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              )}
              {exportingXlsx ? 'Exporting…' : 'Excel Data'}
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ ...S.kpiRow, animation: 'sectionReveal 0.4s ease 0.08s both' }}>
        <KPICard label="Total Revenue"    value={fmtINR(kpis.totalRevenue)}  sub={periodStr} />
        <KPICard label="Avg Order Value"  value={fmtINR(kpis.avgOrderValue)} />
        <KPICard label="Total Quotations" value={kpis.totalQuotations}       />
        <KPICard
          label="Top Customer"
          value={kpis.topCustomer}
          sub={`${kpis.topCustomerShare}% of revenue`}
          accent="#1B5E3B"
        />
        <KPICard
          label="Period Growth"
          value={fmtPct(kpis.periodGrowth)}
          sub="First → last month"
          accent={kpis.periodGrowth == null ? '#9b9b9b' : kpis.periodGrowth >= 0 ? '#059669' : '#dc2626'}
        />
      </div>

      {/* ── Charts Row ── */}
      <div style={{ ...S.chartsRow, animation: 'sectionReveal 0.4s ease 0.16s both' }}>

        {/* Revenue Trend */}
        <div style={S.chartCard}>
          <div style={S.chartTitle}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1B5E3B" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            Revenue Trend
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#1B5E3B" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="#1B5E3B" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="avgGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#f97316" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9b9b9b' }} tickLine={false} />
              <YAxis tickFormatter={fmtY} tick={{ fontSize: 10, fill: '#9b9b9b' }} tickLine={false} axisLine={false} width={52} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="totalRevenue" name="Revenue"     stroke="#1B5E3B" strokeWidth={2} fill="url(#revGrad)" dot={false} />
              <Area type="monotone" dataKey="movingAvg"    name="3-Month Avg" stroke="#f97316" strokeWidth={1.5} strokeDasharray="4 2" fill="url(#avgGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Customer Distribution */}
        <div style={{ ...S.chartCard, flex: '0 0 340px' }}>
          <div style={S.chartTitle}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1B5E3B" strokeWidth="2">
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/>
              <path d="M22 12A10 10 0 0 0 12 2v10z"/>
            </svg>
            Revenue Distribution
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ResponsiveContainer width={170} height={170}>
              <PieChart>
                <Pie data={distribution} cx="50%" cy="50%" innerRadius={48} outerRadius={80}
                  dataKey="value" paddingAngle={2}>
                  {distribution.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5, overflow: 'hidden' }}>
              {distribution.slice(0, 7).map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#4b4b4b' }}>{d.name}</span>
                  <span style={{ color: '#1B5E3B', fontWeight: 600, flexShrink: 0 }}>{d.pct}%</span>
                </div>
              ))}
              {distribution.length > 7 && (
                <div style={{ fontSize: 10, color: '#9b9b9b' }}>+{distribution.length - 7} more</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Status Breakdown ── */}
      {statusBreakdown?.length > 0 && (
        <div style={{ ...S.statusRow, animation: 'sectionReveal 0.4s ease 0.22s both' }}>
          {statusBreakdown.map(s => {
            const colors = { Open: ['#eff6ff','#3b82f6'], Closed: ['#ecfdf5','#059669'], Cancelled: ['#fef2f2','#dc2626'] };
            const [bg, fg] = colors[s.status] || ['#E8F6EE','#1B5E3B'];
            return (
              <div key={s.status} style={{ ...S.statusChip, background: bg }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: fg }}>{s.status}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{s.count}</span>
                <span style={{ fontSize: 11, color: '#9b9b9b' }}>{fmtINR(s.revenue)}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Customer Rankings Table ── */}
      <div style={{ ...S.tableCard, animation: 'sectionReveal 0.4s ease 0.28s both' }}>
        <div style={S.tableHeader}>
          <div style={S.chartTitle}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b6b6b" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
            </svg>
            Customer Rankings
            <span style={S.badge}>{rankings.length} customers</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setRankPage(p => Math.max(0, p - 1))} disabled={rankPage === 0} style={S.pageBtn}>‹</button>
            <span style={{ fontSize: 11, color: '#9b9b9b', alignSelf: 'center' }}>{rankPage + 1} / {Math.ceil(rankings.length / PAGE)}</span>
            <button onClick={() => setRankPage(p => Math.min(Math.ceil(rankings.length / PAGE) - 1, p + 1))} disabled={(rankPage + 1) * PAGE >= rankings.length} style={S.pageBtn}>›</button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Rank','Customer','Revenue','Orders','% Share','Cumulative','Tier'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRankings.map((r, i) => (
                <tr key={r.rank} className="table-row" style={{ background: i % 2 === 0 ? '#fff' : '#f2faf8' }}>
                  <td style={{ ...S.td, color: '#9b9b9b', fontSize: 12 }}>#{r.rank}</td>
                  <td style={{ ...S.td, fontWeight: 500, maxWidth: 200 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.customerName}</div>
                  </td>
                  <td style={{ ...S.td, fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{fmtINR(r.totalRevenue)}</td>
                  <td style={{ ...S.td, textAlign: 'center' }}>{r.quotationCount}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 4, borderRadius: 4, background: '#f0ede8', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(r.pctShare, 100)}%`, height: '100%', background: 'linear-gradient(90deg,#1B5E3B,#2E7D52)', borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 11, color: '#6b6b6b', minWidth: 32 }}>{r.pctShare}%</span>
                    </div>
                  </td>
                  <td style={{ ...S.td, fontSize: 12, color: '#6b6b6b' }}>{r.cumulative}%</td>
                  <td style={{ ...S.td, textAlign: 'center' }}><TierBadge tier={r.tier} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* ── SQL Queries Panel ── */}
      {sqlQueries && (
        <div style={{ ...S.sqlPanel, animation: 'sectionReveal 0.4s ease 0.36s both' }}>
          <button onClick={() => setSqlOpen(o => !o)} style={S.sqlToggle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4CAF7D" strokeWidth="2">
                <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
              </svg>
              <span style={{ color: '#4CAF7D', fontWeight: 600, fontSize: 12 }}>SQL Queries Used</span>
              <span style={{ color: '#6b7280', fontSize: 11 }}>({Object.keys(sqlQueries).length} queries ran in parallel)</span>
            </div>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"
              style={{ transform: sqlOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {sqlOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px 16px' }}>
              {Object.entries(sqlQueries).map(([key, sql]) => (
                <div key={key} style={S.sqlBlock}>
                  <div style={S.sqlBlockHeader}>
                    <span style={{ color: '#4CAF7D', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {key === 'kpis' ? 'KPI Summary' : key === 'trend' ? 'Monthly Trend' : key === 'rankings' ? 'Customer Rankings' : 'Status Breakdown'}
                    </span>
                    <button onClick={() => copySQL(key, sql)} style={S.copyBtn} className="btn-lift">
                      {copied === key ? (
                        <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Copied</>
                      ) : (
                        <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg> Copy</>
                      )}
                    </button>
                  </div>
                  <pre style={S.sqlPre}>{sql}</pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12 },

  headerCard: {
    background: 'linear-gradient(135deg, #E8F6EE, #f0fdf4)',
    border: '1px solid #C8EDD6', borderRadius: 14,
    padding: '18px 20px',
    boxShadow: '0 2px 10px rgba(27,94,59,0.08)'
  },
  headerTitle: { fontSize: 20, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.3px' },
  analyticsIcon: {
    width: 32, height: 32, borderRadius: 9,
    background: 'linear-gradient(135deg,#1B5E3B,#2E7D52)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, boxShadow: '0 4px 10px rgba(27,94,59,0.3)'
  },
  htmlBtn: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: 'linear-gradient(135deg,#1B5E3B,#2E7D52)',
    color: '#fff', border: 'none', borderRadius: 9,
    padding: '9px 18px', fontSize: 13, fontWeight: 600,
    boxShadow: '0 4px 14px rgba(27,94,59,0.3)'
  },
  excelBtn: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: 'linear-gradient(135deg,#059669,#10b981)',
    color: '#fff', border: 'none', borderRadius: 9,
    padding: '9px 18px', fontSize: 13, fontWeight: 600,
    boxShadow: '0 4px 14px rgba(5,150,105,0.3)'
  },
  spinner: {
    width: 14, height: 14, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff',
    display: 'inline-block', animation: 'spin 0.7s linear infinite'
  },

  kpiRow: { display: 'flex', gap: 10 },
  kpiCard: {
    flex: 1, background: '#fff', border: '1px solid #e8e6e0',
    borderRadius: 12, padding: '16px 18px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
  },

  chartsRow: { display: 'flex', gap: 12 },
  chartCard: {
    flex: 1, background: '#fff', border: '1px solid #e8e6e0',
    borderRadius: 12, padding: '16px 18px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
  },
  chartTitle: {
    display: 'flex', alignItems: 'center', gap: 7,
    fontSize: 13, fontWeight: 600, color: '#4b4b4b', marginBottom: 14
  },

  statusRow: { display: 'flex', gap: 10 },
  statusChip: {
    flex: 1, borderRadius: 12, padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: 4,
    border: '1px solid rgba(0,0,0,0.06)'
  },

  tableCard: {
    background: '#fff', border: '1px solid #e8e6e0',
    borderRadius: 12, overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
  },
  tableHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderBottom: '1px solid #f0ede8', background: '#f2faf8'
  },
  badge: {
    background: '#E0F5EE', color: '#1B5E3B', fontSize: 11,
    padding: '2px 9px', borderRadius: 12, fontWeight: 600
  },
  pageBtn: {
    background: '#E8F6EE', border: '1px solid #C8EDD6', borderRadius: 6,
    padding: '4px 10px', fontSize: 14, color: '#1B5E3B', cursor: 'pointer'
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 14px', fontWeight: 600,
    color: '#4b4b4b', background: '#E8F6EE', borderBottom: '1px solid #E0F5EE',
    whiteSpace: 'nowrap', fontSize: 11, position: 'sticky', top: 0
  },
  td: { padding: '9px 14px', color: '#1a1a1a', borderBottom: '1px solid #EAF7F7', whiteSpace: 'nowrap' },

  sqlPanel: {
    background: '#071A0E', borderRadius: 12,
    border: '1px solid #1A4A2E', overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
  },
  sqlToggle: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer',
    borderBottom: '1px solid rgba(61,58,92,0)'
  },
  sqlBlock: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid #1A4A2E',
    borderRadius: 8, overflow: 'hidden'
  },
  sqlBlockHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 14px', borderBottom: '1px solid #1A4A2E',
    background: 'rgba(255,255,255,0.02)'
  },
  copyBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: 'none', border: '1px solid #1A4A2E',
    borderRadius: 6, padding: '3px 9px', color: '#9ca3af', fontSize: 11, cursor: 'pointer'
  },
  sqlPre: {
    padding: '12px 14px', margin: 0, overflowX: 'auto',
    fontSize: 12, lineHeight: 1.7, color: '#e2e8f0',
    fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'pre', background: 'transparent'
  }
};
